import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query'
import { fetchApiWithToken, hydraNextPageParam } from '~/api/utils'
import type { ApplicantHistoryEvent } from '../types'

export type HistoryEntityType = 'applicant' | 'contact'

export type HistoryResponse = {
  'hydra:totalItems': number
  'hydra:member': ApplicantHistoryEvent[]
}

export type HistoryQueryResult = {
  items: ApplicantHistoryEvent[] | undefined
  isLoading: boolean
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
  isError: boolean
  error: Error | null
  totalItems: number
}

type HistoryQueryKey = [
  HistoryEntityType,
  number,
  'history',
  string[],
  boolean,
  boolean
]

export const useHistory = ({
  entityType,
  entityId,
  selectedTypes = new Set<string>(),
  includeCampaigns = false,
  includeContent = false
}: {
  entityType: HistoryEntityType
  entityId: number
  selectedTypes?: Set<string>
  includeCampaigns?: boolean
  includeContent?: boolean
}): HistoryQueryResult => {
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    error
  } = useInfiniteQuery<
    HistoryResponse,
    Error,
    InfiniteData<HistoryResponse>,
    HistoryQueryKey,
    number
  >({
    queryKey: [
      entityType,
      entityId,
      'history',
      Array.from(selectedTypes).sort(),
      includeCampaigns,
      includeContent
    ],
    initialPageParam: 1,
    getNextPageParam: hydraNextPageParam(),
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams()
      params.append(entityType, entityId.toString())
      params.append('page', pageParam.toString())
      if (selectedTypes.size > 0) {
        params.append('types', Array.from(selectedTypes).join(','))
      }
      if (includeCampaigns) {
        params.append('includeCampaigns', 'true')
      }
      if (includeContent) {
        params.append('includeContent', 'true')
      }

      return fetchApiWithToken<HistoryResponse>({
        method: 'get',
        url: `/v1/history?${params.toString()}`,
        useLDJson: true
      })
    },
    enabled: !!entityId,
    staleTime: 300_000
  })

  return {
    items: data?.pages.flatMap((page) => page['hydra:member']),
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    error,
    totalItems: data?.pages[0]?.['hydra:totalItems'] ?? 0
  }
}
